import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LOGO_ACCEPT, LOGO_MAX_BYTES, useChangeCustomerLogo } from "@/hooks/use-customer-logo";

interface CustomerLogoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  hasLogo: boolean;
  /** Forhåndsutfylt nettside (Brønnøysund), om vi har en. */
  suggestedWebsite?: string | null;
}

/**
 * «Endre logo» (VIB-2935): hent fra nettside, last opp et bilde eller fjern
 * logoen. En manuell endring overstyres aldri av nattsynken.
 */
export default function CustomerLogoDialog({ open, onOpenChange, customerId, customerName, hasLogo, suggestedWebsite }: CustomerLogoDialogProps) {
  const change = useChangeCustomerLogo(customerId);
  const [tab, setTab] = useState("website");
  const [website, setWebsite] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const tooLarge = !!file && file.size > LOGO_MAX_BYTES;

  useEffect(() => {
    if (!open) return;
    setTab("website");
    setWebsite(suggestedWebsite ?? "");
    setFile(null);
  }, [open, suggestedWebsite]);

  const run = async (input: Parameters<typeof change.mutateAsync>[0]) => {
    await change.mutateAsync(input);
    onOpenChange(false);
  };
  const busy = change.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Endre logo</DialogTitle>
          <DialogDescription>Logoen til {customerName} vises på kundekortet og i kundelisten.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="website">Hent fra nettside</TabsTrigger>
            <TabsTrigger value="upload">Last opp bilde</TabsTrigger>
          </TabsList>
          <TabsContent value="website">
            <form
              className="space-y-3 py-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (website.trim() && !busy) run({ action: "website", website: website.trim() }).catch(() => undefined);
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="logo-website">Nettside</Label>
                <Input id="logo-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="firma.no" autoComplete="url" />
                <p className="text-xs text-muted-foreground">Vi henter logoen bedriften viser på forsiden sin.</p>
              </div>
              <Button type="submit" disabled={!website.trim() || busy}>{busy ? "Henter …" : "Hent logo"}</Button>
            </form>
          </TabsContent>
          <TabsContent value="upload">
            <form
              className="space-y-3 py-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (file && !tooLarge && !busy) run({ action: "upload", file }).catch(() => undefined);
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="logo-file">Bilde</Label>
                <Input id="logo-file" type="file" accept={LOGO_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                <p className={tooLarge ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
                  {tooLarge ? "Bildet er større enn 512 kB." : "PNG, JPG, SVG eller WebP, maks 512 kB. Bruk en versjon for lys bakgrunn."}
                </p>
              </div>
              <Button type="submit" disabled={!file || tooLarge || busy}>{busy ? "Lagrer …" : "Lagre logo"}</Button>
            </form>
          </TabsContent>
        </Tabs>
        {hasLogo && (
          <DialogFooter className="border-t border-border pt-3 sm:justify-start">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => run({ action: "remove" }).catch(() => undefined)}>
              Fjern logo og vis initialer
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
